import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Spinner } from 'react-bootstrap';
import { useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  User,
  Camera,
  UserCircle,
  Wallet,
  FileText,
  GitBranch,
  Building2,
  BookOpen,
  HelpCircle,
  MessageSquare,
  ShieldCheck,
  Users,
  Download,
  Info,
} from 'lucide-react';
import authService from '../services/authService';
import { toast } from 'react-toastify';

const UserProfile = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [profileImage, setProfileImage] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const profile = await authService.getProfile();
        if (profile) {
          setProfileData(profile);
        }
      } catch (err) {
        toast.error(err.message || 'Failed to load profile', {
          position: 'bottom-left',
          autoClose: 3000,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  useEffect(() => {
    // Load profile image from API data or localStorage
    if (profileData?.profilePicture) {
      setProfileImage(profileData.profilePicture);
      localStorage.setItem('profileImage', profileData.profilePicture);
    } else {
      const savedImage = localStorage.getItem('profileImage');
      if (savedImage) {
        setProfileImage(savedImage);
      }
    }
  }, [profileData]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Show preview immediately
      const imageUrl = URL.createObjectURL(file);
      setProfileImage(imageUrl);

      try {
        const formData = new FormData();
        formData.append('profilePicture', file);

        await authService.updateProfile(formData);
        toast.success('Profile image updated successfully', {
          position: 'bottom-left',
          autoClose: 3000,
        });

        // Refetch profile to get the new image URL from server
        const profile = await authService.getProfile();
        if (profile && profile.profilePicture) {
          setProfileImage(profile.profilePicture);
          localStorage.setItem('profileImage', profile.profilePicture);
        }
      } catch (err) {
        toast.error(err.message || 'Failed to upload profile image', {
          position: 'bottom-left',
          autoClose: 3000,
        });
        // Revert preview on error
        const savedImage = localStorage.getItem('profileImage');
        setProfileImage(savedImage);
      }
    }
  };

  const accountItems = [
    {
      icon: UserCircle,
      title: 'Account Details',
      link: '/account-details',
    },
    { icon: Wallet, title: 'Fund Management', link: '/fund-management' },
    {
      icon: FileText,
      title: 'Pledge Record',
      link: '/pledge-record',
    },
    {
      icon: GitBranch,
      title: 'Commission Order',
      link: '/commission-order',
    },
    { icon: Building2, title: 'Apply for loan', link: '/apply-loan' },
    {
      icon: BookOpen,
      title: 'The loan details',
      link: '/loan-details',
    },
  ];

  const otherItems = [
    { icon: HelpCircle, title: 'Help center', link: '/help-center' },
    {
      icon: MessageSquare,
      title: 'Online service',
      link: '/online-service',
    },
    {
      icon: ShieldCheck,
      title: 'Terms of Service',
      link: '/terms-service',
    },
    { icon: Users, title: 'Cooperation', link: '/cooperation' },
    {
      icon: Download,
      title: 'App download',
      link: '/app-download',
    },
    { icon: Info, title: 'About us', link: '/about-us' },
  ];

  return (
    <div
      style={{
        backgroundColor: '#FFFFFF',
        minHeight: '100vh',
      }}
    >
      {/* Header with full-width background */}
      <div
        style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          width: '100%',
        }}
      >
        <Container
          fluid
          style={{
            maxWidth: isMobile ? '100%' : '85%',
            margin: '0 auto',
            padding: isMobile ? '0' : '0 1rem',
          }}
        >
          <div className='d-flex justify-content-between align-items-center p-3'>
            <Button
              variant='link'
              onClick={() => navigate('/')}
              className='p-0'
              style={{ color: '#FFFFFF', minWidth: '40px' }}
            >
              <ArrowLeft size={24} />
            </Button>
            <h5
              className='mb-0 fw-bold flex-grow-1 text-center'
              style={{ color: '#FFFFFF' }}
            >
              Profile
            </h5>
            <div style={{ width: '40px' }}></div>
          </div>

          {/* Profile Header Section */}
          <div className='text-center text-white px-3 pb-4'>
            <div
              className='position-relative mx-auto mb-3'
              style={{ width: 'fit-content', cursor: 'pointer' }}
              onClick={() => document.getElementById('profile-upload').click()}
            >
              <div
                className='rounded-circle border border-3 border-white d-flex align-items-center justify-content-center mx-auto'
                style={{
                  width: '80px',
                  height: '80px',
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  backgroundImage: profileImage
                    ? `url(${profileImage})`
                    : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              >
                {!profileImage && <User size={32} color='white' />}
              </div>
              <div
                className='position-absolute bg-white rounded-circle d-flex align-items-center justify-content-center'
                style={{
                  width: '28px',
                  height: '28px',
                  bottom: '2px',
                  right: '2px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                }}
              >
                <Camera size={14} color='#667eea' />
              </div>
              <input
                type='file'
                accept='image/*'
                id='profile-upload'
                onChange={handleImageUpload}
                style={{ display: 'none' }}
              />
            </div>

            <div className='mb-2'>
              <h5 className='fw-bold mb-1'>
                {loading ? (
                  <Spinner animation='border' size='sm' />
                ) : (
                  profileData?.email || 'Loading...'
                )}
              </h5>
              <div className='d-flex justify-content-center gap-3 flex-wrap'>
                <span
                  className='badge px-2 py-1 fw-semibold small'
                  style={{
                    background: 'linear-gradient(45deg, #00C851, #007E33)',
                    borderRadius: '15px',
                  }}
                >
                  {loading ? '...' : `VIP${profileData?.vipLevel ?? 0}`}
                </span>
              </div>
            </div>

            <div
              className='row text-center mt-3 mx-0 g-2'
              style={{ maxWidth: '100%' }}
            >
              <div className='col-4 px-1'>
                <div
                  className='fw-bold'
                  style={{ fontSize: '1.1rem', marginBottom: '0.25rem' }}
                >
                  {loading ? (
                    <Spinner animation='border' size='sm' />
                  ) : (
                    profileData?.uid || '1338566'
                  )}
                </div>
                <small
                  className='opacity-75 d-block'
                  style={{ fontSize: '0.7rem' }}
                >
                  UID
                </small>
              </div>
              <div className='col-4 px-1'>
                <div
                  className='fw-bold'
                  style={{ fontSize: '1.1rem', marginBottom: '0.25rem' }}
                >
                  {loading ? (
                    <Spinner animation='border' size='sm' />
                  ) : (
                    profileData?.creditScore ?? 0
                  )}
                </div>
                <small
                  className='opacity-75 d-block'
                  style={{ fontSize: '0.7rem' }}
                >
                  Credit Score
                </small>
              </div>
              <div className='col-4 px-1'>
                <div style={{ marginBottom: '0.25rem' }}>
                  {loading ? (
                    <Spinner animation='border' size='sm' />
                  ) : (
                    <span
                      className='badge px-2 py-1 fw-semibold small'
                      style={{
                        background: 'linear-gradient(45deg, #00C851, #007E33)',
                        borderRadius: '15px',
                      }}
                    >
                      {profileData?.status || 'ACTIVE'}
                    </span>
                  )}
                </div>
                <small
                  className='opacity-75 d-block'
                  style={{ fontSize: '0.7rem' }}
                >
                  Status
                </small>
              </div>
            </div>
          </div>
        </Container>
      </div>

      {/* Main Content with constrained width */}
      <Container
        fluid
        style={{
          maxWidth: isMobile ? '100%' : '85%',
          padding: 'clamp(1rem, 3vw, 1.5rem)',
          backgroundColor: '#FFFFFF',
          minHeight: '100vh',
          margin: '0 auto',
        }}
      >
        <div style={{ paddingBottom: '5rem' }}>
          {/* Account Section */}
          <div className='mb-3'>
            <h6 className='fw-bold mb-3 text-dark'>Account</h6>
            <Row className='g-2 g-md-3'>
              {accountItems.map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <Col xs={6} md={4} key={index}>
                    <Link to={item.link} className='text-decoration-none'>
                      <Card className='h-100 border-0 shadow-sm hover-lift'>
                        <Card.Body className='text-center p-3 p-md-3'>
                          <div
                            className='rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center'
                            style={{
                              width: '55px',
                              height: '55px',
                              background:
                                'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            }}
                          >
                            <IconComponent size={24} color='white' />
                          </div>
                          <div
                            className='text-dark fw-medium'
                            style={{
                              fontSize: '0.95rem',
                              lineHeight: '1.3',
                              minHeight: '2.6rem',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              wordBreak: 'break-word',
                            }}
                          >
                            {item.title}
                          </div>
                        </Card.Body>
                      </Card>
                    </Link>
                  </Col>
                );
              })}
            </Row>
          </div>

          {/* Other Section */}
          <div className='mb-3'>
            <h6 className='fw-bold mb-3 text-dark'>Other</h6>
            <Row className='g-2 g-md-3'>
              {otherItems.map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <Col xs={6} md={4} key={index}>
                    <Link to={item.link} className='text-decoration-none'>
                      <Card className='h-100 border-0 shadow-sm hover-lift'>
                        <Card.Body className='text-center p-3 p-md-3'>
                          <div
                            className='rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center'
                            style={{
                              width: '55px',
                              height: '55px',
                              background:
                                'linear-gradient(135deg, #FF6B6B, #FF8E53)',
                            }}
                          >
                            <IconComponent size={24} color='white' />
                          </div>
                          <div
                            className='text-dark fw-medium'
                            style={{
                              fontSize: '0.95rem',
                              lineHeight: '1.3',
                              minHeight: '2.6rem',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              wordBreak: 'break-word',
                            }}
                          >
                            {item.title}
                          </div>
                        </Card.Body>
                      </Card>
                    </Link>
                  </Col>
                );
              })}
            </Row>
          </div>
        </div>
      </Container>

      <style jsx>{`
        .hover-lift {
          transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
        }
        .hover-lift:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
        }

        @media (max-width: 576px) {
          h5 {
            font-size: 1.1rem !important;
          }
          h6 {
            font-size: 1rem !important;
          }
          .card-body {
            padding: 0.75rem !important;
          }
        }

        @media (max-width: 380px) {
          h5 {
            font-size: 1rem !important;
          }
          h6 {
            font-size: 0.95rem !important;
          }
        }
      `}</style>
    </div>
  );
};

export default UserProfile;
